import React from "react";
import { Outlet, useLocation } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import useSEO from "@/hooks/useSEO";
import useJsonLd from "@/hooks/useJsonLd";
import { SITE } from "@/data/site";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
};

const ORIGIN =
  typeof window !== "undefined" && window.location
    ? window.location.origin
    : "https://genesisappliancerepair.com";

const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "HomeAndConstructionBusiness",
  "@id": `${ORIGIN}/#business`,
  name: SITE.brandFull,
  alternateName: SITE.brand,
  url: ORIGIN,
  telephone: SITE.phone,
  email: SITE.email,
  image: `${ORIGIN}/favicon.svg`,
  description:
    "Premium home appliance repair across the Big Island of Hawaii. Washer, dryer, refrigerator, dishwasher, range, and oven service.",
  priceRange: "$$",
  areaServed: SITE.serviceAreas.map((a) => {
    const [city] = a.split(",");
    return {
      "@type": "City",
      name: city.trim(),
      containedInPlace: {
        "@type": "AdministrativeArea",
        name: "Hawaii County, Hawaii, USA",
      },
    };
  }),
  address: {
    "@type": "PostalAddress",
    addressLocality: "Kailua-Kona",
    addressRegion: "HI",
    addressCountry: "US",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 19.6406,
    longitude: -155.9969,
  },
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "08:00",
      closes: "18:00",
    },
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: "Saturday",
      opens: "08:00",
      closes: "14:00",
    },
  ],
  sameAs: [
    SITE.social.facebook,
    SITE.social.linkedin,
    SITE.social.twitter,
    SITE.social.tiktok,
    SITE.social.youtube,
  ],
  makesOffer: [
    "Washer Repair",
    "Dryer Repair",
    "Refrigerator Repair",
    "Dishwasher Repair",
    "Range & Oven Repair",
  ].map((s) => ({
    "@type": "Offer",
    itemOffered: { "@type": "Service", name: s },
  })),
};

const Layout = () => {
  useSEO();
  useJsonLd("local-business", localBusinessSchema);
  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#111111] flex flex-col">
      <ScrollToTop />
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
};

export default Layout;
